/**
 * Draft an email FROM a task — the entry point the app did not have.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * WHY THIS EXISTS AT ALL, AND WHY IT SHIPS WITH crm#13 AND crm#16
 * ══════════════════════════════════════════════════════════════════════════
 *
 * Before this file, every draft and every touch in the app started from the
 * contact page -- `TouchButtons` renders only on `ContactShow`, and it was the
 * only caller of `logTouch` and `draftEmailAndStamp`. There was no task-scoped
 * entry point anywhere, which is why both bugs took the shape they did: from a
 * screen with no task in hand, the code guessed which task the user meant
 * (crm#13) and guessed which template a step wanted (crm#16), because in that
 * context there was nothing better to do than guess.
 *
 * Fixing the two guesses alone would leave the app unable to close the RIGHT
 * task at all. So the refusals and this control belong in one change: from a
 * contact page a draft closes nothing, and from a task it closes that task and
 * nothing else.
 *
 * WHERE IT LIVES. `tasks/Task.tsx` already renders a dropdown of task actions
 * (postpone, edit, delete) and already keeps a dialog OUTSIDE that menu with
 * its open state in the row -- that is how `TaskEdit` works, and it is not
 * decoration: Radix unmounts `DropdownMenuContent` when the menu closes, so a
 * dialog rendered inside it disappears at the moment it should appear. Hence
 * two exports and two injected lines rather than one component.
 *
 * THE RESOLUTION IS BY ID, ONE HOP, AND NEEDS NO SCHEMA CHANGE:
 *
 *     tasks.enrollment_step_id -> enrollment_steps.content_id -> content_assets
 *
 * `tasks_enrollment_step_id_key` is a UNIQUE partial index, so the middle link
 * is one-to-one. See taskDraft.ts for why the name in `tasks.text` is never
 * used for this.
 */

import { Mail } from "lucide-react";
import { useGetOne } from "ra-core";
import { Link } from "react-router-dom";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";

import type { Contact } from "../types";
import type { ContentAsset } from "./ContentLibrary";
import { primaryEmail } from "./mailto";
import { DraftEmailDialog } from "./TouchButtons";
import type { EnrollmentStep } from "./types";

/**
 * Upstream's `Task` plus the two columns migration 071 added to that table.
 *
 * Declared here rather than on `../types.ts` for the reason marketing/types.ts
 * gives in full: upstream edits that file, and a field we add to it conflicts
 * at whatever rate they do. Both are nullable in Postgres and both are
 * genuinely absent on a hand-made task, which is the ordinary case.
 */
export type TaskFromStep = {
  id: number | string;
  contact_id: number | string;
  text?: string;
  done_date?: string | null;
  enrollment_step_id?: number | null;
};

/**
 * The menu entry. Renders inside `Task.tsx`'s existing `DropdownMenuContent`.
 *
 * Offered on EVERY task, including one with no step and one already done. A
 * task with no step opens the dialog with nothing preselected -- the same
 * refusal the contact page makes -- and a done task cannot be closed twice
 * (closeTask leaves an existing `done_date` alone), so neither case needs the
 * item hidden. Hiding it would instead mean the action appears and disappears
 * for reasons a person cannot see.
 */
export const TaskDraftEmailItem = ({ onSelect }: { onSelect: () => void }) => (
  <DropdownMenuItem
    className="cursor-pointer h-12 md:h-8 px-4 md:px-2 text-base md:text-sm"
    onClick={onSelect}
  >
    Draft email
  </DropdownMenuItem>
);

/**
 * The dialog behind that entry, with this task's own content and this task's
 * own id attached.
 *
 * MOUNTED ONLY WHILE OPEN, which is `TaskEdit`'s own shape four lines below
 * the injection in `Task.tsx` -- `{open && <EditBase …>}`.
 *
 * WHY, HONESTLY: this component is rendered once per task ROW, so a list of
 * twenty tasks would otherwise hold sixty idle query subscriptions. Keeping it
 * mounted with `{ enabled: open }` on each `useGetOne` was written first and
 * WORKS -- checked, because an earlier version of this comment claimed it did
 * not: with the gated version restored, taskDraftUi.test.tsx passes 3/3. The
 * apparent failure was a stale DOM node in the test, not this. So this is a
 * choice about how much a task list holds open, not a repair, and it is
 * recorded as one.
 */
export const TaskDraftEmailDialog = ({
  task,
  open,
  onOpenChange,
}: {
  task: TaskFromStep;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  if (!open) return null;
  return <OpenTaskDraftEmailDialog task={task} onOpenChange={onOpenChange} />;
};

const OpenTaskDraftEmailDialog = ({
  task,
  onOpenChange,
}: {
  task: TaskFromStep;
  onOpenChange: (open: boolean) => void;
}) => {
  const { data: contact, isPending: contactPending } = useGetOne<Contact>(
    "contacts",
    { id: task.contact_id },
  );

  const stepId = task.enrollment_step_id ?? null;
  const { data: step } = useGetOne<EnrollmentStep>(
    "enrollment_steps",
    { id: stepId as number },
    // A hand-made task has no step, and that is the ordinary case rather than
    // a missing row -- there is nothing to fetch and nothing to preselect.
    { enabled: stepId != null },
  );

  const contentId = step?.content_id ?? null;
  const { data: stepAsset } = useGetOne<ContentAsset>(
    "content_assets",
    { id: contentId as number },
    // Null is legitimate: a "call them, no script" step names no template.
    { enabled: contentId != null },
  );

  const email = primaryEmail(contact);

  /*
   * THE TWO STATES THE SHARED DIALOG CANNOT REPRESENT get their own small one,
   * rather than nothing happening when the item is clicked. A control that
   * silently does nothing teaches the person clicking it that the app is
   * broken -- the same argument the disabled Draft email button on the contact
   * page makes, and the reason that button explains itself instead.
   */
  if (!contact || !email) {
    return (
      <Dialog open onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md max-h-[calc(100%-2rem)] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Draft an email</DialogTitle>
            <DialogDescription>
              {contactPending || !contact
                ? "Looking up who this task is about…"
                : "This contact has no email address on file, so there is nothing to draft."}
            </DialogDescription>
          </DialogHeader>
          {contact && !email && (
            // A blocked action links to its blocker. House rule; the same
            // sentence the contact page ends with.
            <Button asChild variant="outline" size="sm">
              <Link to={`/contacts/${contact.id}/show`}>
                <Mail className="size-4" /> Add an address
              </Link>
            </Button>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <DraftEmailDialog
      contact={contact}
      email={email}
      open
      onOpenChange={onOpenChange}
      step={step ?? null}
      stepAsset={stepAsset ?? null}
      // THE WHOLE POINT. This task, by id, from the row that was clicked --
      // never a task found by searching the contact's list.
      task={task}
    />
  );
};
